import type { Logger } from "@expert/logging";

import { customCleanEnv, url } from "envalid";
import ky, { isHTTPError } from "ky";

import { featureCache } from "./featureCache";
import { hashInfo } from "./hashInfo";

/**
 * The one warn shape this module logs. Declared as a single signature rather than reusing
 * `Pick<Logger, "warn">`, because that method is overloaded and TypeScript requires an implementation
 * to satisfy every overload, which no precise signature can do. A full {@link Logger} is assignable
 * to this, so {@link setFeatureLogger} still accepts one.
 */
interface FeatureLogger {
    warn(payload: { error?: unknown; key: string }, message: string): void;
}

// Delegate at call time so spies on console.warn (e.g. in tests) still observe calls.
let logger: FeatureLogger = {
    warn: (payload, message) => {
        console.warn(payload, message);
    },
};

const env = customCleanEnv(
    import.meta.env,
    {
        VITE_API_K8S_FEATURE_FLAG_URL: url({
            default: "https://feature.remoteexpert.npr.aws.asurion.net/api",
        }),
    },
    (
        inputEnv,
    ): {
        apiK8sFeatureFlagUrl: string;
    } => ({
        apiK8sFeatureFlagUrl: inputEnv.VITE_API_K8S_FEATURE_FLAG_URL,
    }),
);

export function setFeatureLogger(newLogger: Logger) {
    logger = newLogger;
}

/** One result {@link fetchFeature} returned, as handed to the registered {@link FeatureReporter}. */
export interface FeatureReport {
    /** The key the caller passed, i.e. the real config name after any short-name mapping. */
    featureKey: string;
    result: FeatureResult<unknown>;
    context: Record<string, unknown>;
}

/**
 * Called synchronously with every result just before {@link fetchFeature} returns it, so it must be cheap and must
 * not block. A throw is caught and logged and never reaches the caller.
 */
export type FeatureReporter = (report: FeatureReport) => void;

let reporter: FeatureReporter | undefined;

/** Lets the host app observe every flag result (e.g. to send it to analytics) without this package depending on it. */
export function setFeatureReporter(newReporter: FeatureReporter | undefined) {
    reporter = newReporter;
}

function report(featureKey: string, result: FeatureResult<unknown>, context: Record<string, unknown>) {
    if (!reporter) return;
    try {
        reporter({ featureKey, result, context });
    } catch (error) {
        logger.warn({ error, key: featureKey }, "feature reporter failed");
    }
}

/**
 * `ok: false` never means the rule evaluated to a falsy value; that is `ok: true`. The two `ok: false`
 * reasons want opposite handling, so callers branch on `reason`:
 * - `notConfigured`: the service answered 404, which is what it returns for a key with no config in this
 *   environment. That is normal while a flag's config is still rolling out, so callers usually keep their default.
 * - `failed`: any other error (network, timeout, non-404 status, bad body). This is an outage signal.
 */
export type FeatureResult<T> =
    | { ok: true; value: T }
    | { ok: false; reason: "notConfigured" }
    | { ok: false; reason: "failed"; error: unknown };

/*
 * key - feature key to fetch
 * context - context about the requester to determine the value of the key
 * cacheTtl - ttl of in-memory cache to reduce load on the feature toggle server (in seconds)
 */
export const fetchFeature = async <T>(
    key: string,
    context: Record<string, unknown>,
    cacheTtl = 60,
): Promise<FeatureResult<T>> => {
    // One result for every path (cache hit, fetch, 404, failure) so the reporter sees each exactly once.
    let result: FeatureResult<T>;
    try {
        const hashedInfo = await hashInfo(key + JSON.stringify(context));

        if (cacheTtl > 0 && featureCache.has(hashedInfo)) {
            result = { ok: true, value: featureCache.get<T>(hashedInfo) as T };
        } else {
            const { value } = await ky
                .post(`${env.apiK8sFeatureFlagUrl}/v1/feature/${key}/fetch`, {
                    json: { context: { ...context } },
                    retry: { limit: 3, methods: ["POST"], retryOnTimeout: true, jitter: true, delay: () => 0 },
                })
                .json<{ value: T }>();

            featureCache.set(hashedInfo, value, cacheTtl);
            result = { ok: true, value };
        }
    } catch (error) {
        if (isHTTPError(error) && error.response.status === 404) {
            logger.warn({ key }, "feature key is not configured in this environment");
            result = { ok: false, reason: "notConfigured" };
        } else {
            logger.warn({ error, key }, "error fetching the feature key");
            result = { ok: false, reason: "failed", error };
        }
    }
    report(key, result, context);
    return result;
};

/** Collapses both `ok: false` reasons to `undefined`. Use {@link fetchFeature} when the caller must tell those apart. */
export const getFeature = async <T>(
    key: string,
    context: Record<string, unknown>,
    cacheTtl = 60,
): Promise<T | undefined> => {
    const result = await fetchFeature<T>(key, context, cacheTtl);
    return result.ok ? result.value : undefined;
};
